import React, { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import '../assets/css/common.css';
import DocumentBody from '../components/DocumentBody';
import { documentService } from '../services/documentService';
import { removeAllSpacesInString } from '../utils/regex';

const SAMPLE_NAME = '山田 太郎';
// 書面本文では姓名間のスペースが詰められるため、注記の表記も実際の描画に合わせる
const SAMPLE_NAME_IN_BODY = removeAllSpacesInString(SAMPLE_NAME);

function formatDateTime(value) {
  if (!value) return '-';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '-';
  return `${d.getFullYear()}/${String(d.getMonth() + 1).padStart(2, '0')}/${String(d.getDate()).padStart(2, '0')} ` +
    `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

function DocumentEdit() {
  const { key } = useParams();

  const [title, setTitle] = useState('');
  const [versions, setVersions] = useState([]);
  const [currentVersion, setCurrentVersion] = useState(null);
  const [body, setBody] = useState('');
  const [changeNote, setChangeNote] = useState('');
  const [requiresResign, setRequiresResign] = useState(true);
  const [previewVersion, setPreviewVersion] = useState(null);

  const [loading, setLoading] = useState(true);
  const [publishing, setPublishing] = useState(false);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState('');

  const load = useCallback(async () => {
    try {
      const history = await documentService.getVersions(key);
      setTitle(history.title);
      setVersions(history.versions || []);

      const latestPublished = (history.versions || []).find((v) => v.isPublished);
      if (latestPublished) {
        const detail = await documentService.getVersion(key, latestPublished.version);
        setCurrentVersion(detail);
        setBody(detail.body);
      }
    } catch (err) {
      setError(err?.data?.error || '書面の取得に失敗しました。');
    } finally {
      setLoading(false);
    }
  }, [key]);

  useEffect(() => {
    load();
  }, [load]);

  const isUnchanged = currentVersion && currentVersion.body === body;

  const handlePublish = async () => {
    if (!body.trim()) {
      setError('本文は必須です。');
      return;
    }
    if (isUnchanged) {
      setError('本文が現行バージョンと同一です。変更してから公開してください。');
      return;
    }

    const nextVersion = currentVersion ? currentVersion.version + 1 : 1;
    const confirmed = window.confirm(
      requiresResign
        ? `v${nextVersion}として公開し、旧版に署名済みのメンバーへ再署名依頼メールを送信します。よろしいですか？`
        : `v${nextVersion}として公開します（再署名依頼メールは送信しません）。よろしいですか？`
    );
    if (!confirmed) return;

    setPublishing(true);
    setError(null);
    setMessage('');
    try {
      const result = await documentService.publishVersion(key, {
        body,
        changeNote: changeNote || null,
        requiresResign
      });
      setMessage(
        requiresResign
          ? `${result.message} 再署名依頼メールを${result.notified}名に送信しました。`
          : result.message
      );
      setChangeNote('');
      await load();
    } catch (err) {
      setError(err?.data?.error || '書面の公開に失敗しました。');
    } finally {
      setPublishing(false);
    }
  };

  const handlePreviewVersion = async (version) => {
    if (previewVersion && previewVersion.version === version) {
      setPreviewVersion(null);
      return;
    }
    try {
      setPreviewVersion(await documentService.getVersion(key, version));
    } catch (err) {
      setError('バージョンの取得に失敗しました。');
    }
  };

  if (loading) {
    return <div className="container" style={{ padding: 24 }}><p className="text-muted">読み込み中...</p></div>;
  }

  return (
    <div className="container" style={{ maxWidth: 1100, margin: '0 auto', padding: 24 }}>
      <h1 className="text-primary text-center mb-4">{title || '書面'}</h1>

      {error && <div className="alert alert-danger">{error}</div>}
      {message && <div className="alert alert-success">{message}</div>}

      <p className="text-muted">
        現行バージョン: {currentVersion ? `v${currentVersion.version}（${formatDateTime(currentVersion.publishedAt)} 公開）` : '未公開'}
      </p>

      <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', alignItems: 'flex-start' }}>
        <div style={{ flex: '1 1 420px', minWidth: 320 }}>
          <h2 className="mb-2">本文の編集</h2>
          <textarea
            className="form-control"
            style={{ minHeight: 480, fontFamily: 'monospace', fontSize: 13 }}
            value={body}
            onChange={(e) => setBody(e.target.value)}
            aria-label="書面本文"
          />
          <small className="text-muted d-block mt-1">
            記法: 「# 見出し」「- 箇条書き」（先頭2スペースで入れ子）。<code>{'{{name}}'}</code> は署名者の氏名に置き換わります。
          </small>
        </div>

        <div style={{ flex: '1 1 420px', minWidth: 320 }}>
          <h2 className="mb-2">プレビュー</h2>
          <div
            style={{
              border: '1px solid #dee2e6',
              borderRadius: 6,
              padding: 16,
              minHeight: 480,
              maxHeight: 640,
              overflowY: 'auto',
              background: '#fff'
            }}
          >
            <DocumentBody body={body} values={{ name: SAMPLE_NAME }} />
          </div>
          <small className="text-muted">氏名は「{SAMPLE_NAME_IN_BODY}」を仮に当てはめて表示しています。</small>
        </div>
      </div>

      <h2 className="mt-4 mb-2">改訂して公開</h2>
      <div className="mb-3">
        <label className="form-label" htmlFor="change-note">改訂内容メモ</label>
        <input
          id="change-note"
          className="form-control"
          type="text"
          maxLength={500}
          value={changeNote}
          onChange={(e) => setChangeNote(e.target.value)}
          placeholder="例: 第4項に画像の利用範囲を追記"
        />
        <small className="text-muted">再署名依頼メールにも記載されます。</small>
      </div>
      <div className="mb-3">
        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={requiresResign}
            onChange={(e) => setRequiresResign(e.target.checked)}
          />
          旧版に署名済みのメンバーに再署名を求める（メールでお知らせします）
        </label>
      </div>
      <button className="btn btn-primary" onClick={handlePublish} disabled={publishing || isUnchanged}>
        {publishing ? '公開中...' : '新しいバージョンとして公開'}
      </button>
      {isUnchanged && <div className="text-muted text-small mt-1">本文が現行バージョンと同じため公開できません。</div>}

      <h2 className="mt-4 mb-2">バージョン履歴</h2>
      <table className="table">
        <thead>
          <tr>
            <th>バージョン</th>
            <th>公開日時</th>
            <th>改訂内容</th>
            <th>再署名</th>
            <th>作成者</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {versions.map((version) => (
            <tr key={version.id}>
              <td>v{version.version}{version.version === currentVersion?.version && <span className="text-small text-muted">（現行）</span>}</td>
              <td>{formatDateTime(version.publishedAt)}</td>
              <td>{version.changeNote || '-'}</td>
              <td>{version.requiresResign ? '要' : '不要'}</td>
              <td>{version.createdByName || '-'}</td>
              <td>
                <button className="btn btn-secondary btn-sm" onClick={() => handlePreviewVersion(version.version)}>
                  {previewVersion?.version === version.version ? '閉じる' : 'プレビュー'}
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {previewVersion && (
        <div style={{ border: '1px solid #dee2e6', borderRadius: 6, padding: 16, background: '#fff' }}>
          <h3>v{previewVersion.version} のプレビュー</h3>
          <DocumentBody body={previewVersion.body} values={{ name: SAMPLE_NAME }} />
        </div>
      )}

      <div className="mt-4">
        <Link to="/admin/documents" className="btn btn-secondary">書面一覧に戻る</Link>
      </div>
    </div>
  );
}

export default DocumentEdit;
